import { Link } from 'react-router-dom';
import './App.css';
import lumadriveIcon from './assets/LumaDrive_Icon.png';
import premiumIcon from './assets/LumaDrive_Premium_Icon.png';

const APP_STORE_URL = 'https://apps.apple.com/us/app/lumadrive/id6764681954';

function App() {
  return (
    <div className="app">
      <Nav />
      <main>
        <Hero />
        <Marquee />
        <Features />
        <HowItWorks />
        <Privacy />
        <Pricing />
        <FAQ />
        <FinalCTA />
      </main>
      <Footer />
    </div>
  );
}

function Nav() {
  return (
    <header className="nav">
      <a href="#top" className="nav-brand">
        <img src={lumadriveIcon} alt="" />
        <span>LumaDrive</span>
      </a>
      <nav className="nav-links" aria-label="Primary">
        <a href="#features">Features</a>
        <a href="#how">How it works</a>
        <a href="#privacy">Privacy</a>
        <a href="#pricing">Pricing</a>
        <Link to="/support">Support</Link>
      </nav>
      <a
        href={APP_STORE_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="nav-cta"
      >
        Get app
        <span aria-hidden="true">→</span>
      </a>
    </header>
  );
}

function Hero() {
  return (
    <section className="hero" id="top">
      <div className="hero-bg" aria-hidden="true">
        <div className="hero-grid" />
        <div className="hero-glow hero-glow-a" />
        <div className="hero-glow hero-glow-b" />
      </div>
      <div className="hero-inner">
        <div className="hero-text">
          <span className="badge">
            <span className="badge-dot" />
            iPhone dashcam, no extra hardware
          </span>
          <h1>
            Your iPhone is now a
            <br />
            <span className="grad-text">full-featured dashcam.</span>
          </h1>
          <p className="lede">
            LumaDrive records every drive with continuous loop capture, GPS &amp; speed logging,
            incident auto-lock, and a HUD burned right into your footage — all stored privately on
            your device. Always recording. Always protecting.
          </p>
          <div className="hero-ctas">
            <a
              className="btn btn-primary"
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              <AppleIcon />
              Download on the App Store
            </a>
            <a className="btn btn-ghost" href="#features">
              See how it works
            </a>
          </div>
          <div className="hero-meta">
            <Stat number="$3.99" label="per month" />
            <span className="hero-meta-divider" />
            <Stat number="$29.99" label="lifetime" />
            <span className="hero-meta-divider" />
            <Stat number="100%" label="on-device" />
          </div>
        </div>
        <div className="hero-visual">
          <Dashcam />
        </div>
      </div>
    </section>
  );
}

function Stat({ number, label }) {
  return (
    <div className="stat">
      <div className="stat-num">{number}</div>
      <div className="stat-lbl">{label}</div>
    </div>
  );
}

function Dashcam() {
  return (
    <div className="dashcam">
      <div className="dashcam-frame">
        <div className="dashcam-screen">
          <svg
            className="scene"
            viewBox="0 0 400 240"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <defs>
              <linearGradient id="sky" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#0a0820" />
                <stop offset="55%" stopColor="#3a1f4f" />
                <stop offset="85%" stopColor="#c9603e" />
                <stop offset="100%" stopColor="#fdb56b" />
              </linearGradient>
              <linearGradient id="road" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#241c34" />
                <stop offset="100%" stopColor="#070611" />
              </linearGradient>
              <radialGradient id="sun" cx="0.5" cy="0.5" r="0.5">
                <stop offset="0%" stopColor="#fde9b8" stopOpacity="1" />
                <stop offset="60%" stopColor="#fdb56b" stopOpacity="0.4" />
                <stop offset="100%" stopColor="#fdb56b" stopOpacity="0" />
              </radialGradient>
            </defs>
            <rect x="0" y="0" width="400" height="120" fill="url(#sky)" />
            <ellipse cx="200" cy="120" rx="160" ry="24" fill="url(#sun)" />
            <path
              d="M0,118 L30,113 L52,116 L72,108 L96,112 L122,106 L150,116 L182,114 L218,118 L256,110 L290,114 L322,108 L350,116 L378,112 L400,118 L400,120 L0,120 Z"
              fill="#1a0b2a"
              opacity="0.85"
            />
            <polygon points="0,240 400,240 240,120 160,120" fill="url(#road)" />
            <line
              x1="200"
              y1="120"
              x2="80"
              y2="240"
              stroke="rgba(255,255,255,0.18)"
              strokeWidth="1"
            />
            <line
              x1="200"
              y1="120"
              x2="320"
              y2="240"
              stroke="rgba(255,255,255,0.18)"
              strokeWidth="1"
            />
            <line
              x1="200"
              y1="120"
              x2="200"
              y2="240"
              stroke="#fdd58a"
              strokeWidth="1.4"
              strokeDasharray="4 10"
              opacity="0.65"
              className="lane-anim"
            />
          </svg>

          <div className="hud hud-tl">
            <span className="rec-dot" />
            <span className="rec-text mono">REC&nbsp;&nbsp;00:14:32</span>
          </div>
          <div className="hud hud-tr">
            <div className="hud-label">Date · Time</div>
            <div className="hud-value mono">2026-05-01</div>
            <div className="hud-value mono">14:32:07</div>
          </div>
          <div className="hud hud-bl">
            <div className="speed">
              <span className="speed-num">47</span>
              <span className="speed-unit">MPH</span>
            </div>
            <div className="heading mono">↑ N · 003°</div>
          </div>
          <div className="hud hud-br">
            <div className="hud-label">GPS</div>
            <div className="hud-value mono">37.7749° N</div>
            <div className="hud-value mono">122.4194° W</div>
          </div>
        </div>
        <div className="dashcam-glare" aria-hidden="true" />
      </div>
      <div className="dashcam-caption">
        Burned-in HUD — exactly as it appears in every saved clip.
      </div>
    </div>
  );
}

function Marquee() {
  const items = [
    'Continuous loop',
    'GPS & speed logged',
    'Incident auto-lock',
    'Burned-in HUD',
    'On-device only',
    'Smart storage limits',
    'Built-in recycling bin',
    '30-min free trial',
  ];
  return (
    <div className="marquee" aria-hidden="true">
      <div className="marquee-track">
        {[...items, ...items].map((t, i) => (
          <span key={i} className="marquee-item">
            <span className="marquee-dot" />
            {t}
          </span>
        ))}
      </div>
    </div>
  );
}

function Features() {
  return (
    <section className="section features" id="features">
      <div className="section-head">
        <span className="eyebrow">Features</span>
        <h2>
          Everything a real dashcam does. <span className="grad-text">None of the hardware.</span>
        </h2>
        <p className="section-lede">
          Six purpose-built capabilities turn your iPhone into a dashcam you can rely on. Set it up
          once and forget it — LumaDrive does the rest.
        </p>
      </div>

      <div className="bento">
        <article className="card card-feature card-loop">
          <div className="card-icon">
            <LoopIcon />
          </div>
          <h3>Continuous loop recording</h3>
          <p>
            LumaDrive records in rotating clips so storage never fills up. When space runs out, the
            oldest clips are automatically removed — exactly like a real dashcam. You're always
            capturing what's happening right now.
          </p>
          <div className="loop-art" aria-hidden="true">
            <div className="loop-clip" />
            <div className="loop-clip" />
            <div className="loop-clip is-active" />
            <div className="loop-clip" />
            <div className="loop-clip" />
          </div>
        </article>

        <article className="card card-feature">
          <div className="card-icon">
            <GPSIcon />
          </div>
          <h3>GPS &amp; speed logging</h3>
          <p>
            Every clip is stamped with speed, heading, and GPS coordinates in real time. Precise,
            geo-tagged evidence — automatically, no setup required.
          </p>
          <div className="chip-row">
            <span className="chip mono">47 MPH</span>
            <span className="chip mono">↑ N 003°</span>
            <span className="chip mono">37.77° N</span>
          </div>
        </article>

        <article className="card card-feature">
          <div className="card-icon">
            <ShieldIcon />
          </div>
          <h3>
            Incident detection <span className="pill">Beta</span>
          </h3>
          <p>
            LumaDrive listens to the accelerometer for sudden impacts. When a crash or hard brake is
            detected, the current clip locks instantly and can never be auto-deleted — preserving
            the moment that matters most.
          </p>
        </article>

        <article className="card card-feature card-overlay">
          <div className="card-icon">
            <LayersIcon />
          </div>
          <h3>Overlay burning</h3>
          <p>
            Permanently embed a HUD directly into your footage — date, time, GPS coordinates,
            speed, and heading baked right into the video so it's always visible no matter where
            the clip is shared.
          </p>
          <div className="overlay-art" aria-hidden="true">
            <svg
              className="overlay-scene"
              viewBox="0 0 400 160"
              preserveAspectRatio="xMidYMid slice"
            >
              <defs>
                <linearGradient id="ovsky" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#0a0820" />
                  <stop offset="55%" stopColor="#3a1f4f" />
                  <stop offset="90%" stopColor="#c9603e" />
                  <stop offset="100%" stopColor="#fdb56b" />
                </linearGradient>
                <linearGradient id="ovroad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#241c34" />
                  <stop offset="100%" stopColor="#070611" />
                </linearGradient>
                <radialGradient id="ovsun" cx="0.5" cy="0.5" r="0.5">
                  <stop offset="0%" stopColor="#fde9b8" />
                  <stop offset="60%" stopColor="#fdb56b" stopOpacity="0.4" />
                  <stop offset="100%" stopColor="#fdb56b" stopOpacity="0" />
                </radialGradient>
              </defs>
              <rect x="0" y="0" width="400" height="80" fill="url(#ovsky)" />
              <ellipse cx="200" cy="80" rx="140" ry="18" fill="url(#ovsun)" />
              <polygon points="0,160 400,160 230,80 170,80" fill="url(#ovroad)" />
              <line x1="200" y1="80" x2="100" y2="160" stroke="rgba(255,255,255,0.15)" strokeWidth="1" />
              <line x1="200" y1="80" x2="300" y2="160" stroke="rgba(255,255,255,0.15)" strokeWidth="1" />
              <line
                x1="200"
                y1="80"
                x2="200"
                y2="160"
                stroke="#fdd58a"
                strokeWidth="1.2"
                strokeDasharray="3 8"
                opacity="0.65"
              />
            </svg>
            <div className="overlay-hud overlay-hud-tl">
              <span className="rec-dot" />
              <span className="mono">REC</span>
            </div>
            <div className="overlay-hud overlay-hud-tr mono">2026-05-01 14:32</div>
            <div className="overlay-hud overlay-hud-bl">
              <span className="overlay-speed mono">47</span>
              <span className="overlay-unit">MPH</span>
            </div>
            <div className="overlay-hud overlay-hud-br mono">37.77° N</div>
          </div>
        </article>

        <article className="card card-feature">
          <div className="card-icon">
            <LockIcon />
          </div>
          <h3>Footage stays on your device</h3>
          <p>
            All recordings are stored privately on your iPhone. Nothing is uploaded to the cloud,
            shared with third parties, or sent anywhere. Your footage is yours, period.
          </p>
        </article>

        <article className="card card-feature">
          <div className="card-icon">
            <DiskIcon />
          </div>
          <h3>Smart storage management</h3>
          <p>
            Set your own storage limit. Enable auto-overwrite to keep recording indefinitely, or
            let LumaDrive stop when the limit is reached. A built-in recycling bin gives you a
            safety net before clips are gone for good.
          </p>
        </article>
      </div>

      <div className="beta-note">
        <strong>About the Beta.</strong> Incident detection may not catch every event. Don't rely
        solely on automatic detection in emergency situations.
      </div>
    </section>
  );
}

function HowItWorks() {
  return (
    <section className="section how" id="how">
      <div className="section-head">
        <span className="eyebrow">How it works</span>
        <h2>Three taps from install to driving.</h2>
      </div>
      <ol className="steps">
        <li className="step">
          <div className="step-num">01</div>
          <h3>Mount your iPhone</h3>
          <p>
            Use any standard car mount with a clear forward view. Plug in for power, and you're set
            for long drives.
          </p>
        </li>
        <li className="step">
          <div className="step-num">02</div>
          <h3>Tap record</h3>
          <p>
            LumaDrive starts a continuous loop, stamps every clip with GPS &amp; speed, and listens
            for impacts in the background.
          </p>
        </li>
        <li className="step">
          <div className="step-num">03</div>
          <h3>Drive — that's it</h3>
          <p>
            If something happens, the clip locks itself. Otherwise, your storage cycles
            automatically. Review or export any time.
          </p>
        </li>
      </ol>
    </section>
  );
}

function Privacy() {
  return (
    <section className="section privacy" id="privacy">
      <div className="privacy-card">
        <div className="privacy-text">
          <span className="eyebrow">Privacy</span>
          <h2>
            Your footage <span className="grad-text">never leaves your iPhone.</span>
          </h2>
          <p>
            No cloud uploads. No third-party servers. No analytics on your clips. Every recording
            lives only on your device, and only you decide what to do with it.
          </p>
          <ul className="privacy-list">
            <li>
              <Check /> Recordings stored locally on device
            </li>
            <li>
              <Check /> No iCloud backup, no cloud sync
            </li>
            <li>
              <Check /> No third-party sharing, ever
            </li>
            <li>
              <Check /> You can export or delete clips any time
            </li>
          </ul>
          <div className="privacy-warn">
            <strong>Heads up:</strong> deleting LumaDrive permanently erases all footage. Export
            anything you want to keep before removing the app — recordings are not backed up to
            iCloud.
          </div>
        </div>
        <div className="privacy-shield" aria-hidden="true">
          <BigShield />
        </div>
      </div>
    </section>
  );
}

function Pricing() {
  return (
    <section className="section pricing" id="pricing">
      <div className="section-head">
        <span className="eyebrow">Pricing</span>
        <h2>
          Try it free. Or own it for <span className="grad-text">$29.99 once.</span>
        </h2>
        <p className="section-lede">
          All footage playback, sharing, file management, and organization are <em>always free</em>{' '}
          — no subscription required to access your recordings.
        </p>
      </div>
      <div className="plans">
        <article className="plan">
          <div className="plan-head">
            <h3>Free</h3>
            <p className="plan-price">
              <span className="plan-amount">$0</span>
              <small>forever</small>
            </p>
            <p className="plan-desc">Try LumaDrive risk-free.</p>
          </div>
          <ul className="plan-list">
            <li>
              <Check /> 30 minutes of recording
            </li>
            <li>
              <Check /> Free playback &amp; export, always
            </li>
            <li>
              <Check /> Local-only storage
            </li>
            <li>
              <Check /> File management &amp; organization
            </li>
          </ul>
          <a
            className="btn btn-ghost btn-block"
            href={APP_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            Download free
          </a>
        </article>

        <article className="plan">
          <div className="plan-head">
            <h3>Premium</h3>
            <p className="plan-price">
              <span className="plan-amount">$3.99</span>
              <small>per month</small>
            </p>
            <p className="plan-desc">Unlock the full dashcam, monthly.</p>
          </div>
          <ul className="plan-list">
            <li>
              <Check /> Unlimited recording time
            </li>
            <li>
              <Check /> Continuous loop recording
            </li>
            <li>
              <Check /> GPS &amp; speed logging
            </li>
            <li>
              <Check /> Incident auto-lock
            </li>
            <li>
              <Check /> Overlay burning
            </li>
            <li>
              <Check /> Cancel any time in Apple ID Settings
            </li>
          </ul>
          <a
            className="btn btn-ghost btn-block"
            href={APP_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            Start 30-min free trial
          </a>
        </article>

        <article className="plan plan-featured">
          <div className="plan-flag">Best value</div>
          <div className="plan-head">
            <img src={premiumIcon} alt="" className="plan-icon" />
            <h3>Lifetime</h3>
            <p className="plan-price">
              <span className="plan-amount">$29.99</span>
              <small>one-time</small>
            </p>
            <p className="plan-desc">Pay once. Own it forever.</p>
          </div>
          <ul className="plan-list">
            <li>
              <Check /> Everything in Premium, forever
            </li>
            <li>
              <Check /> Unlimited recording time
            </li>
            <li>
              <Check /> Continuous loop recording
            </li>
            <li>
              <Check /> GPS &amp; speed logging
            </li>
            <li>
              <Check /> Incident auto-lock
            </li>
            <li>
              <Check /> Overlay burning
            </li>
            <li>
              <Check /> No recurring charges, ever
            </li>
          </ul>
          <a
            className="btn btn-primary btn-block"
            href={APP_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            Get Lifetime — $29.99
          </a>
          <p className="plan-fine">Pays for itself in under 8 months vs. monthly.</p>
        </article>
      </div>
    </section>
  );
}

const faqs = [
  {
    q: 'Where is my footage stored?',
    a: 'All recordings are stored locally on your iPhone. Nothing is uploaded to the cloud, sent to third parties, or backed up to iCloud. The only copy of your footage is the one on your device.',
  },
  {
    q: 'What happens if I delete the app?',
    a: "Deleting LumaDrive permanently erases all footage. Export any clips you want to keep before removing the app — once they're gone, they're gone.",
  },
  {
    q: 'How accurate is incident detection?',
    a: "Incident detection is currently in Beta. It uses your iPhone's accelerometer to detect sudden impacts and hard brakes, locking the current clip when triggered. It may not catch every event, so don't rely on it alone in an emergency.",
  },
  {
    q: 'How does loop recording handle storage?',
    a: 'You set the storage limit. With auto-overwrite enabled, the oldest clips are removed when the limit is reached so recording continues indefinitely. Or you can have LumaDrive stop recording when the limit is hit. Either way, deleted clips first land in a recycling bin so nothing is lost by accident.',
  },
  {
    q: 'Can I cancel Premium any time?',
    a: 'Yes. Manage and cancel your monthly subscription any time in Apple ID Settings. Your existing footage stays accessible — playback and export are always free, no subscription required.',
  },
  {
    q: "What's the Lifetime purchase?",
    a: "Lifetime is a one-time $29.99 purchase that unlocks every Premium feature forever — no monthly billing, no renewals. It pays for itself in under 8 months compared to the monthly plan, and you keep full access for as long as LumaDrive is on your device.",
  },
  {
    q: 'What overlays are burned into the video?',
    a: "Date, time, GPS coordinates, speed, and heading. Because the overlay is baked into the video itself, it stays with the clip wherever it's shared — no separate metadata file required.",
  },
];

function FAQ() {
  return (
    <section className="section faq" id="faq">
      <div className="section-head">
        <span className="eyebrow">FAQ</span>
        <h2>Questions, answered.</h2>
      </div>
      <div className="faq-list">
        {faqs.map((f) => (
          <details key={f.q} className="faq-item">
            <summary>
              <span className="faq-q">{f.q}</span>
              <span className="faq-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </span>
            </summary>
            <p>{f.a}</p>
          </details>
        ))}
      </div>
      <div className="faq-more">
        <Link to="/support" className="btn btn-ghost">
          Browse all support questions
          <span className="btn-arrow" aria-hidden="true">→</span>
        </Link>
      </div>
    </section>
  );
}

function FinalCTA() {
  return (
    <section className="section final-cta" id="download">
      <div className="cta-card">
        <div className="cta-bg" aria-hidden="true" />
        <img src={lumadriveIcon} alt="" className="cta-icon" />
        <h2>
          Drive protected. <span className="grad-text">Starting today.</span>
        </h2>
        <p>
          Download LumaDrive on iPhone, record free for 30 minutes, then go unlimited —
          $3.99/month or $29.99 once for lifetime access.
        </p>
        <div className="cta-buttons">
          <a
            className="btn btn-primary btn-lg"
            href={APP_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            <AppleIcon />
            Download on the App Store
          </a>
          <a className="btn btn-ghost btn-lg" href="#features">
            Explore features
          </a>
        </div>
        <p className="cta-fine">
          Recordings stay on your device. They're not backed up to iCloud — export anything you
          want to keep before removing the app.
        </p>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="footer">
      <div className="footer-inner">
        <div className="footer-brand">
          <img src={lumadriveIcon} alt="" />
          <div>
            <strong>LumaDrive</strong>
            <span>Private dashcam for iPhone.</span>
          </div>
        </div>
        <div className="footer-cols">
          <div>
            <h4>Product</h4>
            <a href="#features">Features</a>
            <a href="#how">How it works</a>
            <a href="#pricing">Pricing</a>
          </div>
          <div>
            <h4>Legal</h4>
            <Link to="/legal">Terms &amp; Privacy</Link>
            <Link to="/privacy">Privacy Policy</Link>
            <Link to="/terms">Terms of Use</Link>
          </div>
          <div>
            <h4>Get LumaDrive</h4>
            <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">
              App Store
            </a>
            <a href="#pricing">Premium</a>
            <Link to="/support">Support &amp; FAQ</Link>
          </div>
        </div>
      </div>
      <div className="footer-bottom">
        <span>© {new Date().getFullYear()} LumaDrive. All rights reserved.</span>
        <span className="footer-bottom-links">
          <Link to="/privacy">Privacy</Link>
          <span aria-hidden="true">·</span>
          <Link to="/terms">Terms</Link>
          <span aria-hidden="true">·</span>
          <span>Built for safer, more private driving.</span>
        </span>
      </div>
    </footer>
  );
}

function AppleIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="currentColor"
      aria-hidden="true"
      className="apple-icon"
    >
      <path d="M17.5 12.6c0-2.4 2-3.6 2.1-3.6-1.2-1.7-3-1.9-3.6-2-1.6-.2-3.1.9-3.9.9-.8 0-2.1-.9-3.4-.9-1.8 0-3.4 1-4.3 2.6-1.8 3.2-.5 7.9 1.3 10.5.9 1.3 1.9 2.7 3.3 2.6 1.3-.1 1.8-.8 3.4-.8s2 .8 3.4.8c1.4 0 2.3-1.3 3.2-2.6 1-1.5 1.4-2.9 1.4-3-.1 0-2.9-1.1-2.9-4.5zM14.9 5.2c.7-.9 1.2-2.1 1.1-3.2-1 0-2.3.7-3 1.5-.6.8-1.2 2-1.1 3.1 1.1.1 2.3-.5 3-1.4z" />
    </svg>
  );
}

function Check() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" className="check">
      <circle cx="12" cy="12" r="10" fill="rgba(139,92,246,0.18)" />
      <path
        d="M7.5 12.5l3 3 6-6"
        fill="none"
        stroke="#c4b5fd"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function LoopIcon() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 12a9 9 0 0 1 15.5-6.3" />
      <path d="M21 5v5h-5" />
      <path d="M21 12a9 9 0 0 1-15.5 6.3" />
      <path d="M3 19v-5h5" />
    </svg>
  );
}

function GPSIcon() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 21s-7-7.4-7-12a7 7 0 1 1 14 0c0 4.6-7 12-7 12z" />
      <circle cx="12" cy="9" r="2.4" />
    </svg>
  );
}

function ShieldIcon() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 3l8 3v6c0 5-3.5 8.5-8 9-4.5-.5-8-4-8-9V6l8-3z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  );
}

function LayersIcon() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 3l9 5-9 5-9-5 9-5z" />
      <path d="M3 13l9 5 9-5" />
      <path d="M3 18l9 5 9-5" />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="4" y="11" width="16" height="10" rx="2" />
      <path d="M8 11V8a4 4 0 0 1 8 0v3" />
    </svg>
  );
}

function DiskIcon() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <ellipse cx="12" cy="6" rx="8" ry="3" />
      <path d="M4 6v6c0 1.7 3.6 3 8 3s8-1.3 8-3V6" />
      <path d="M4 12v6c0 1.7 3.6 3 8 3s8-1.3 8-3v-6" />
    </svg>
  );
}

function BigShield() {
  return (
    <svg viewBox="0 0 200 220" width="100%" height="100%" aria-hidden="true">
      <defs>
        <linearGradient id="bigshield" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#a78bfa" />
          <stop offset="100%" stopColor="#38bdf8" />
        </linearGradient>
      </defs>
      <path
        d="M100 14l72 24v60c0 44-34 80-72 86-38-6-72-42-72-86V38l72-24z"
        fill="url(#bigshield)"
        opacity="0.14"
      />
      <path
        d="M100 26l60 20v52c0 36-28 66-60 72-32-6-60-36-60-72V46l60-20z"
        fill="none"
        stroke="url(#bigshield)"
        strokeWidth="1.4"
      />
      <path
        d="M100 46l44 14v40c0 26-21 48-44 52-23-4-44-26-44-52V60l44-14z"
        fill="rgba(139,92,246,0.06)"
        stroke="url(#bigshield)"
        strokeWidth="0.8"
      />
      <path
        d="M76 110l16 16 32-34"
        fill="none"
        stroke="url(#bigshield)"
        strokeWidth="3.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default App;
